/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from 'react'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'
import { enqueueSnackbar } from 'notistack'
import { UndocumentedScan, Scan } from 'common/types'
import { useParams } from 'react-router-dom'
import { SubTrayItemProductDetails } from 'lib/services/api/product-service/receiveItems/types'
import { InventoryProviderClient } from 'lib/apollo/inventory-config'
import { useAuth0 } from 'app/Auth'
import { GET_SUB_TRAY_ITEM_BY_ID, EDIT_SUB_TRAY_ITEM } from 'lib/apollo/schema'
import { CustomImplant } from 'views/SPDLayout/SPD/SPD.types'

export const useReturnDTMScrewToTray = () => {
  const { surgeryId } = useParams()
  const { isAuthenticated } = useAuth0()

  // Create a direct client instance
  const inventoryClient = isAuthenticated ? InventoryProviderClient() : null

  const returnScrewToTray = useCallback(
    async (scan: UndocumentedScan | Scan) => {
      // Check if client is available (user is authenticated)
      if (!inventoryClient) {
        console.error('Cannot return screw to tray: User not authenticated')
        return false
      }

      // Check if scan is a DTM screw and has the necessary data
      if (!scan.isDTMScrew || !scan.dtmScrewData || !surgeryId) {
        return false
      }

      const dtmScrewData = scan.dtmScrewData as Scan['dtmScrewData']

      // Check if trayId exists
      if (!dtmScrewData || !dtmScrewData.trayId) {
        console.warn('DTM screw missing trayId', scan)
        return false
      }

      try {
        // Use the client directly to make queries
        const { data } = await inventoryClient.query({
          query: GET_SUB_TRAY_ITEM_BY_ID,
          variables: { subTrayItemId: Number(dtmScrewData.trayId) },
        })

        if (!data?.getSubTrayItemById) {
          console.error(
            'Failed to get sub-tray data for DTM screw',
            dtmScrewData.trayId
          )
          return false
        }

        const subTrayItem = data.getSubTrayItemById
        const trayDetails = extractSubTrayJSONData(subTrayItem)

        if (!trayDetails) {
          console.error(
            'Failed to extract tray details from sub-tray',
            subTrayItem
          )
          return false
        }

        // Determine if this is a plate, screw, or custom implant
        const isPlate =
          dtmScrewData.label && !dtmScrewData.row && !dtmScrewData.column
        const isCustomImplant =
          dtmScrewData.wasted !== undefined && !dtmScrewData.label

        if (isCustomImplant) {
          // Handle custom implant return
          return handleCustomImplantReturn(
            scan,
            dtmScrewData,
            trayDetails,
            subTrayItem.id,
            surgeryId,
            inventoryClient
          )
        } else if (isPlate) {
          // Handle plate return
          return handlePlateReturn(
            scan,
            dtmScrewData,
            trayDetails,
            subTrayItem.id,
            inventoryClient
          )
        } else {
          // Handle screw return
          return handleScrewReturn(
            scan,
            dtmScrewData,
            trayDetails,
            subTrayItem.id,
            surgeryId,
            inventoryClient
          )
        }
      } catch (error) {
        console.error('Error returning DTM screw to tray:', error)
        enqueueSnackbar('Failed to return screw to tray', { variant: 'error' })
        return false
      }
    },
    [inventoryClient, surgeryId]
  )

  const updateDTMScrewWastedStatus = useCallback(
    async (scan: UndocumentedScan | Scan, isWasted: boolean) => {
      // Check if client is available (user is authenticated)
      if (!inventoryClient) {
        console.error('Cannot update screw status: User not authenticated')
        return false
      }

      // Check if scan is a DTM screw and has the necessary data
      if (!scan.isDTMScrew || !scan.dtmScrewData || !surgeryId) {
        return false
      }

      const dtmScrewData = scan.dtmScrewData as Scan['dtmScrewData']

      // Check if trayId exists
      if (!dtmScrewData || !dtmScrewData.trayId) {
        console.warn('DTM screw missing trayId', scan)
        return false
      }

      try {
        // Use the client directly to make queries
        const { data } = await inventoryClient.query({
          query: GET_SUB_TRAY_ITEM_BY_ID,
          variables: { subTrayItemId: Number(dtmScrewData.trayId) },
        })

        if (!data?.getSubTrayItemById) {
          console.error(
            'Failed to get sub-tray data for DTM screw',
            dtmScrewData.trayId
          )
          return false
        }

        const subTrayItem = data.getSubTrayItemById
        const trayDetails = extractSubTrayJSONData(subTrayItem)

        if (!trayDetails) {
          console.error(
            'Failed to extract tray details from sub-tray',
            subTrayItem
          )
          return false
        }

        // Determine if this is a plate or a screw
        const isPlate =
          dtmScrewData.label && !dtmScrewData.row && !dtmScrewData.column

        if (!isPlate) {
          // Handle plate wasted status update
          return updateScrewWastedStatus(
            dtmScrewData,
            trayDetails,
            subTrayItem.id,
            surgeryId,
            isWasted,
            inventoryClient
          )
        }
      } catch (error) {
        console.error('Error updating DTM screw wasted status:', error)
        enqueueSnackbar('Failed to update screw status in tray', {
          variant: 'error',
        })
        return false
      }
    },
    [inventoryClient, surgeryId]
  )

  const updateScrewWastedStatus = async (
    dtmScrewData: Scan['dtmScrewData'],
    trayDetails: SubTrayItemProductDetails,
    subTrayItemId: number,
    surgeryId: string,
    isWasted: boolean,
    inventoryClient: any
  ) => {
    if (!dtmScrewData) {
      console.error('DTM screw data is undefined')
      return false
    }

    // Get the nurse usage history for this surgery
    const nurseUsageHistory = trayDetails.nurseUsageHistory || {}
    const nurseUsageHistoryForSurgery = nurseUsageHistory[surgeryId] || {
      plates: [],
      screws: [],
      otherItems: [],
    }

    // Find the screw in the nurse usage history
    const screwIndex = nurseUsageHistoryForSurgery.screws?.findIndex(
      (screw) =>
        screw.label === dtmScrewData.label &&
        screw.row === dtmScrewData.row &&
        screw.x === dtmScrewData.column
    )

    if (screwIndex === undefined || screwIndex === -1) {
      console.warn('Screw not found in nurse usage history', dtmScrewData)
      return false
    }

    // Update the wasted status of the screw
    const updatedScrews = [...nurseUsageHistoryForSurgery.screws]
    updatedScrews[screwIndex] = {
      ...updatedScrews[screwIndex],
      wasted: isWasted,
    }

    const updatedNurseUsageHistory = {
      ...nurseUsageHistory,
      [surgeryId]: {
        ...nurseUsageHistoryForSurgery,
        screws: updatedScrews,
      },
    }

    // Update the tray with the modified nurse usage history
    const newProductDetails = JSON.stringify({
      ...trayDetails,
      nurseUsageHistory: updatedNurseUsageHistory,
    })

    // Use the client directly for mutations
    const editResult = await inventoryClient.mutate({
      mutation: EDIT_SUB_TRAY_ITEM,
      variables: {
        input: {
          id: subTrayItemId,
          productDetails: newProductDetails,
        },
      },
    })

    if (editResult?.data?.editSubTrayItem) {
      enqueueSnackbar(
        `Screw ${
          isWasted ? 'marked as wasted' : 'marked as implanted'
        } in tray`,
        {
          variant: 'success',
        }
      )
      return true
    } else {
      console.error('Failed to update screw wasted status in tray')
      return false
    }
  }

  const handleScrewReturn = async (
    scan: UndocumentedScan | Scan,
    dtmScrewData: Scan['dtmScrewData'],
    trayDetails: SubTrayItemProductDetails,
    subTrayItemId: number,
    surgeryId: string,
    inventoryClient: any
  ) => {
    if (!dtmScrewData) {
      console.error('DTM screw data is undefined')
      return false
    }

    // Create the screw object to add back to the tray
    const screwToReturn = {
      label: dtmScrewData.label,
      row: dtmScrewData.row,
      column: dtmScrewData.size, // dtmScrewData.size maps to trayScrew.column
      x: dtmScrewData.column, // dtmScrewData.column maps to trayScrew.x
      deviceId: scan.deviceId,
      deviceDescription: scan.deviceDescription,
      wasted: false,
    }

    // Check if the screw is already in the tray
    const screwAlreadyExists = trayDetails.screws.some(
      (screw) =>
        screw.label === screwToReturn.label &&
        screw.row === screwToReturn.row &&
        screw.x === screwToReturn.x
    )

    if (screwAlreadyExists) {
      return true
    }

    // Add the screw back to the tray
    const updatedScrews = [...trayDetails.screws, screwToReturn]

    // Update the nurse usage history to remove the screw from the surgery
    const nurseUsageHistory = trayDetails.nurseUsageHistory
    const nurseUsageHistoryForSurgery = nurseUsageHistory[surgeryId] || {
      plates: [],
      screws: [],
      otherItems: [],
    }

    const updatedNurseUsageHistory: SubTrayItemProductDetails['nurseUsageHistory'] =
      {
        ...nurseUsageHistory,
        [surgeryId]: {
          ...nurseUsageHistoryForSurgery,
          screws: nurseUsageHistoryForSurgery.screws
            ? nurseUsageHistoryForSurgery.screws.filter(
                (screw) =>
                  !(
                    screw.x === screwToReturn.x &&
                    screw.row === screwToReturn.row &&
                    screw.label === screwToReturn.label
                  )
              )
            : [],
        },
      }

    // Update the tray with the returned screw
    const newProductDetails = JSON.stringify({
      ...trayDetails,
      screws: updatedScrews,
      nurseUsageHistory: updatedNurseUsageHistory,
    })

    // Use the client directly for mutations
    const editResult = await inventoryClient.mutate({
      mutation: EDIT_SUB_TRAY_ITEM,
      variables: {
        input: {
          id: subTrayItemId,
          productDetails: newProductDetails,
        },
      },
    })

    if (editResult?.data?.editSubTrayItem) {
      enqueueSnackbar('Screw returned to tray successfully', {
        variant: 'success',
      })
      return true
    } else {
      console.error('Failed to update sub-tray with returned screw')
      return false
    }
  }

  const handlePlateReturn = async (
    scan: UndocumentedScan | Scan,
    dtmScrewData: Scan['dtmScrewData'],
    trayDetails: SubTrayItemProductDetails,
    subTrayItemId: number,
    inventoryClient: any
  ) => {
    if (!dtmScrewData) {
      console.error('DTM screw data is undefined')
      return false
    }

    if (!surgeryId) {
      console.error('Surgery ID is undefined')
      return false
    }

    // Find the plate in the tray
    const plateName = dtmScrewData.label
    const existingPlateIndex = trayDetails.plates.findIndex(
      (plate) => plate.plateName === plateName
    )

    let updatedPlates = [...trayDetails.plates]

    if (existingPlateIndex >= 0) {
      // Increase the count of the existing plate
      updatedPlates[existingPlateIndex] = {
        ...updatedPlates[existingPlateIndex],
        plateCount: updatedPlates[existingPlateIndex].plateCount + 1,
      }
    } else {
      // Add a new plate if it doesn't exist
      updatedPlates.push({
        plateName,
        plateCount: 1,
        deviceId: scan.deviceId || undefined,
        deviceDescription: scan.deviceDescription || undefined,
      })
    }

    // Update the nurse usage history to remove the plate from the surgery
    const nurseUsageHistory = trayDetails.nurseUsageHistory || {}
    const nurseUsageHistoryForSurgery = nurseUsageHistory[surgeryId] || {
      plates: [],
      screws: [],
      otherItems: [],
    }

    // Find the first plate with matching plateName to remove
    const plateIndex = nurseUsageHistoryForSurgery.plates?.findIndex(
      (plate) => plate.plateName === plateName
    )

    let updatedNurseUsageHistory = { ...nurseUsageHistory }

    if (plateIndex !== undefined && plateIndex >= 0) {
      // Create a new plates array without the first matching plate
      const updatedPlatesUsage = [
        ...nurseUsageHistoryForSurgery.plates.slice(0, plateIndex),
        ...nurseUsageHistoryForSurgery.plates.slice(plateIndex + 1),
      ]

      updatedNurseUsageHistory = {
        ...nurseUsageHistory,
        [surgeryId]: {
          ...nurseUsageHistoryForSurgery,
          plates: updatedPlatesUsage,
        },
      }
    }

    // Update the tray with the returned plate
    const newProductDetails = JSON.stringify({
      ...trayDetails,
      plates: updatedPlates,
      nurseUsageHistory: updatedNurseUsageHistory,
    })

    // Use the client directly for mutations
    const editResult = await inventoryClient.mutate({
      mutation: EDIT_SUB_TRAY_ITEM,
      variables: {
        input: {
          id: subTrayItemId,
          productDetails: newProductDetails,
        },
      },
    })

    if (editResult?.data?.editSubTrayItem) {
      enqueueSnackbar('Plate returned to tray successfully', {
        variant: 'success',
      })
      return true
    } else {
      console.error('Failed to update sub-tray with returned plate')
      return false
    }
  }

  const handleCustomImplantReturn = async (
    scan: UndocumentedScan | Scan,
    dtmScrewData: Scan['dtmScrewData'],
    trayDetails: SubTrayItemProductDetails,
    subTrayItemId: number,
    surgeryId: string,
    inventoryClient: any
  ) => {
    if (!dtmScrewData) {
      console.error('DTM screw data is undefined')
      return false
    }

    // Create the custom implant object to add back to the tray
    const implantToReturn: CustomImplant = {
      deviceId: scan.deviceId || '',
      deviceDescription: scan.deviceDescription || '',
    }

    // Add the implant back to the tray
    const updatedCustomImplants = [
      ...(trayDetails.customImplants || []),
      implantToReturn,
    ]

    // Update the nurse usage history to remove the implant from the surgery
    const nurseUsageHistory = trayDetails.nurseUsageHistory
    const nurseUsageHistoryForSurgery = nurseUsageHistory[surgeryId] || {
      plates: [],
      screws: [],
      otherItems: [],
      customImplants: [],
    }

    // Find the first matching custom implant in the nurse usage history
    const implantIndex = nurseUsageHistoryForSurgery.customImplants?.findIndex(
      (implant) =>
        implant.deviceId === implantToReturn.deviceId &&
        implant.wasted === dtmScrewData.wasted
    )

    let updatedNurseUsageHistory = { ...nurseUsageHistory }

    if (
      implantIndex !== undefined &&
      implantIndex >= 0 &&
      nurseUsageHistoryForSurgery.customImplants
    ) {
      // Create a new customImplants array without the first matching implant
      const updatedCustomImplantsUsage = [
        ...nurseUsageHistoryForSurgery.customImplants.slice(0, implantIndex),
        ...nurseUsageHistoryForSurgery.customImplants.slice(implantIndex + 1),
      ]

      updatedNurseUsageHistory = {
        ...nurseUsageHistory,
        [surgeryId]: {
          ...nurseUsageHistoryForSurgery,
          customImplants: updatedCustomImplantsUsage,
        },
      }
    }

    // Update the tray with the returned implant
    const newProductDetails = JSON.stringify({
      ...trayDetails,
      customImplants: updatedCustomImplants,
      nurseUsageHistory: updatedNurseUsageHistory,
    })

    // Use the client directly for mutations
    const editResult = await inventoryClient.mutate({
      mutation: EDIT_SUB_TRAY_ITEM,
      variables: {
        input: {
          id: subTrayItemId,
          productDetails: newProductDetails,
        },
      },
    })

    if (editResult?.data?.editSubTrayItem) {
      enqueueSnackbar('Custom implant returned to tray successfully', {
        variant: 'success',
      })
      return true
    } else {
      console.error('Failed to update sub-tray with returned custom implant')
      return false
    }
  }

  return { returnScrewToTray, updateDTMScrewWastedStatus }
}
